'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Mover {
  symbol: string;
  name: string;
  exchange: string;
  sector: string;
  price: number | null;
  change: number | null;
  changePct: number | null;
  volume: string | null;
  marketCap: string | null;
  aiScore: number | null;
  aiSignal: string | null;
  alertCount: number;
  hasData: boolean;
}

const SIGNAL_STYLES: Record<string, string> = {
  BUY: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  SELL: 'text-red-400 bg-red-500/10 border-red-500/30',
  HOLD: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  WATCH: 'text-sky-400 bg-sky-500/10 border-sky-500/30',
};

type SortKey = 'symbol' | 'price' | 'changePct' | 'volume' | 'aiScore';

interface TopMoversTableProps {
  filter: string;
}

export default function TopMoversTable({ filter }: TopMoversTableProps) {
  const [movers, setMovers] = useState<Mover[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('changePct');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [flashMap, setFlashMap] = useState<Record<string, 'up' | 'down'>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const supabase = createClient();

  const fetchMovers = async () => {
    try {
      const res = await fetch('/api/market/movers');
      const json = await res.json();
      if (json.movers) {
        setMovers(prev => {
          const next: Mover[] = json.movers.map((m: any) => ({
            symbol: m.symbol,
            name: m.name,
            exchange: m.exchange,
            sector: m.sector,
            price: m.price,
            change: m.change,
            changePct: m.changePct,
            volume: m.volume ? `${(m.volume / 1e6).toFixed(1)}M` : null,
            marketCap: m.marketCap,
            aiScore: m.aiScore,
            aiSignal: m.aiSignal,
            alertCount: m.alertCount || 0,
            hasData: m.hasData,
          }));

          const flashes: Record<string, 'up' | 'down'> = {};
          next.forEach(n => {
            const old = prev.find(p => p.symbol === n.symbol);
            if (old && n.price != null && old.price != null && n.price !== old.price) {
              flashes[n.symbol] = n.price > old.price ? 'up' : 'down';
            }
          });
          if (Object.keys(flashes).length > 0) {
            setFlashMap(flashes);
            setTimeout(() => setFlashMap({}), 500);
          }
          return next;
        });
      }
    } catch (err) {
      // Keep state on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovers();

    const channel = supabase
      .channel('market_prices_movers')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'market_prices' }, () => {
        fetchMovers();
      })
      .subscribe();

    const pollId = setInterval(fetchMovers, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollId);
    };
  }, []);

  const filtered = movers.filter(m => {
    if (filter === 'gainers') return (m.changePct ?? 0) > 0;
    if (filter === 'losers') return (m.changePct ?? 0) < 0;
    if (filter === 'ai_signal') return m.aiSignal === 'BUY' || m.aiSignal === 'SELL';
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleRow = (symbol: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(symbol) ? next.delete(symbol) : next.add(symbol);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedRows(checked ? new Set(sorted.map(m => m.symbol)) : new Set());
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <Icon
      name={sortKey === col ? (sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon') : 'ChevronUpDownIcon'}
      size={12}
      variant="outline"
      className={sortKey === col ? 'text-sky-400' : 'text-slate-600'}
    />
  );

  const SORT_COLS: Array<[SortKey, string]> = [
    ['symbol', 'Symbol'], ['price', 'Price'], ['changePct', 'Change %'],
    ['volume', 'Volume'], ['aiScore', 'AI Score'],
  ];

  const hasData = movers.some(m => m.hasData);

  return (
    <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#334155]">
        <div className="flex items-center gap-2">
          <Icon name="ArrowsUpDownIcon" size={16} variant="solid" className="text-sky-500" />
          <span className="text-sm font-semibold text-slate-200 font-mono">Top Movers</span>
          {!loading && <span className="text-xs text-slate-500 font-mono">({sorted.length})</span>}
          {loading && <div className="w-3 h-3 border border-sky-500 border-t-transparent rounded-full animate-spin" />}
        </div>
        {selectedRows.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-mono">{selectedRows.size} selected</span>
            <button className="flex items-center gap-1 px-2 py-1 bg-sky-500/20 border border-sky-500/30 rounded text-xs text-sky-400 hover:bg-sky-500/30 transition-all min-h-[28px]">
              <Icon name="BellIcon" size={12} variant="outline" />
              Bulk Alert
            </button>
            <button onClick={() => setSelectedRows(new Set())} className="text-xs text-slate-500 hover:text-slate-300 transition-all">Clear</button>
          </div>
        )}
      </div>

      {!loading && !hasData && (
        <div className="px-4 py-3 bg-amber-500/5 border-b border-[#334155]">
          <p className="text-xs text-amber-400 font-mono">
            ⚠ Market data unavailable — configure an API key in{' '}
            <a href="/user-settings" className="underline hover:text-amber-300">Settings → API Configuration</a>
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#334155] bg-[#0F172A]/50">
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  className="w-3 h-3 accent-sky-500"
                  checked={selectedRows.size === sorted.length && sorted.length > 0}
                  onChange={e => toggleAll(e.target.checked)}
                />
              </th>
              {SORT_COLS.map(([key, label]) => (
                <th
                  key={key}
                  onClick={() => handleSort(key)}
                  className="px-3 py-2 text-left text-slate-400 font-mono cursor-pointer hover:text-slate-200 select-none whitespace-nowrap"
                >
                  <div className="flex items-center gap-1">{label}<SortIcon col={key} /></div>
                </th>
              ))}
              <th className="px-3 py-2 text-left text-slate-400 font-mono whitespace-nowrap">Signal</th>
              <th className="px-3 py-2 text-left text-slate-400 font-mono whitespace-nowrap">Alerts</th>
              <th className="px-3 py-2 text-left text-slate-400 font-mono whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && !loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-600 font-mono text-xs">
                  No market data available
                </td>
              </tr>
            ) : (
              sorted.map(m => {
                const flash = flashMap[m.symbol];
                const isPos = (m.changePct ?? 0) >= 0;
                const isSelected = selectedRows.has(m.symbol);
                return (
                  <tr
                    key={m.symbol}
                    className={`border-b border-[#334155]/50 transition-all duration-300 hover:bg-[#334155]/30 ${
                      isSelected ? 'bg-sky-500/5' : ''
                    } ${flash === 'up' ? 'bg-emerald-500/5' : flash === 'down' ? 'bg-red-500/5' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <input type="checkbox" className="w-3 h-3 accent-sky-500" checked={isSelected} onChange={() => toggleRow(m.symbol)} />
                    </td>
                    <td className="px-3 py-2">
                      <div>
                        <span className="font-mono font-bold text-sky-400">{m.symbol}</span>
                        <p className="text-slate-500 truncate max-w-[140px]">{m.name}</p>
                      </div>
                    </td>
                    <td className={`px-3 py-2 font-mono font-bold transition-colors duration-300 ${
                      flash === 'up' ? 'text-emerald-400' : flash === 'down' ? 'text-red-400' : 'text-slate-200'
                    }`}>
                      {m.price != null ? `$${m.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-600">—</span>}
                    </td>
                    <td className={`px-3 py-2 font-mono font-bold ${m.changePct != null ? (isPos ? 'text-emerald-400' : 'text-red-400') : 'text-slate-600'}`}>
                      {m.changePct != null ? `${isPos ? '+' : ''}${m.changePct.toFixed(2)}%` : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-400">{m.volume ?? <span className="text-slate-600">—</span>}</td>
                    <td className="px-3 py-2">
                      {m.aiScore != null ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 h-1.5 bg-[#334155] rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${m.aiScore >= 70 ? 'bg-sky-500' : m.aiScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${m.aiScore}%` }}
                            />
                          </div>
                          <span className="font-mono text-slate-400">{m.aiScore}</span>
                        </div>
                      ) : (
                        <span className="text-slate-600 font-mono">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {m.aiSignal ? (
                        <span className={`px-1.5 py-0.5 rounded border text-xs font-mono font-bold ${SIGNAL_STYLES[m.aiSignal] || 'text-slate-400 bg-slate-500/10 border-slate-500/30'}`}>
                          {m.aiSignal}
                        </span>
                      ) : (
                        <span className="text-slate-600 font-mono">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {m.alertCount > 0 ? (
                        <span className="flex items-center gap-1 text-amber-400 font-mono">
                          <Icon name="BellAlertIcon" size={12} variant="solid" />
                          {m.alertCount}
                        </span>
                      ) : (
                        <span className="text-slate-600 font-mono">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        href={`/asset-analysis-screen?symbol=${m.symbol}`}
                        className="flex items-center gap-1 text-sky-400 hover:text-sky-300 font-mono transition-all"
                      >
                        <Icon name="ArrowTopRightOnSquareIcon" size={12} variant="outline" />
                        Analyze
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}